/**
 * Filters product gallery media by filename tokens: __variant-{slug}
 * Listens to Dawn PUB_SUB_EVENTS.variantChange; does not alter price/cart/buy logic.
 */
(function () {
  const HIDDEN_CLASS = 'fn-variant-media--hidden';
  const DATA_SCRIPT_PREFIX = 'fn-variant-media-data-';

  function normalizeToken(token) {
    if (!token) return null;
    return String(token)
      .toLowerCase()
      .replace(/_\d+x$/i, '')
      .replace(/^-+|-+$/g, '');
  }

  function extractVariantToken(filenameOrUrl) {
    if (!filenameOrUrl) return null;
    const name = String(filenameOrUrl).split('/').pop().split('?')[0];
    const match = name.match(/__variant-([a-z0-9-]+)/i);
    if (!match) return null;
    return normalizeToken(match[1]);
  }

  function slugify(value) {
    if (!value) return '';
    return String(value)
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function parseDataScript(script) {
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[fn-variant-media] Invalid JSON data', error);
      return null;
    }
  }

  function getMediaIdFromDomKey(domKey, sectionId) {
    if (!domKey) return null;
    const prefix = `${sectionId}-`;
    if (domKey.startsWith(prefix)) {
      return domKey.slice(prefix.length);
    }
    return domKey;
  }

  function findVariant(data, variantId) {
    if (!data?.variants?.length) return null;
    const id = variantId != null ? String(variantId) : null;
    if (id) {
      const byId = data.variants.find((variant) => String(variant.id) === id);
      if (byId) return byId;
    }
    return data.variants.find((variant) => String(variant.id) === String(data.selectedVariantId)) || data.variants[0];
  }

  function buildVariantSlugs(variant) {
    const slugs = new Set();
    (variant?.slugs || []).forEach((slug) => {
      const normalized = normalizeToken(slug) || slugify(slug);
      if (normalized) slugs.add(normalized);
    });
    if (variant?.title) {
      const titleSlug = slugify(variant.title);
      if (titleSlug) slugs.add(titleSlug);
    }
    (variant?.options || []).forEach((option) => {
      const optionSlug = slugify(option);
      if (optionSlug) slugs.add(optionSlug);
    });
    return slugs;
  }

  function buildMediaLookup(data) {
    const lookup = new Map();
    (data.media || []).forEach((item) => {
      const token = normalizeToken(item.variantToken) || extractVariantToken(item.filename) || extractVariantToken(item.url);
      lookup.set(String(item.id), {
        ...item,
        variantToken: token,
      });
    });
    return lookup;
  }

  function getGalleryRoot(sectionId) {
    return (
      document.getElementById(`MediaGallery-${sectionId}`) ||
      document.querySelector(`media-gallery[id^="MediaGallery-${sectionId}"]`) ||
      document.querySelector('media-gallery')
    );
  }

  function setItemVisibility(element, visible) {
    if (!element) return;
    element.classList.toggle(HIDDEN_CLASS, !visible);
    if (visible) {
      element.removeAttribute('hidden');
    } else {
      element.setAttribute('hidden', 'true');
    }
  }

  function filterGallery(data, variant) {
    const gallery = getGalleryRoot(data.sectionId);
    if (!gallery) return;

    if (!data.hasVariantTokens) {
      gallery.querySelectorAll('[data-media-id], [data-target]').forEach((el) => setItemVisibility(el, true));
      return;
    }

    const mediaLookup = buildMediaLookup(data);
    const slugs = buildVariantSlugs(variant);
    const featuredMediaId = variant?.featuredMediaId != null ? String(variant.featuredMediaId) : null;
    const visibleMediaIds = new Set();

    gallery.querySelectorAll('[data-media-id]').forEach((item) => {
      const mediaId = getMediaIdFromDomKey(item.dataset.mediaId, data.sectionId);
      const meta = mediaLookup.get(String(mediaId));
      const token = meta?.variantToken || null;

      let visible = true;
      if (token) {
        visible = slugs.has(token) || (featuredMediaId && String(mediaId) === featuredMediaId);
      }

      setItemVisibility(item, visible);
      if (visible && mediaId) visibleMediaIds.add(String(mediaId));
    });

    gallery.querySelectorAll('[data-target]').forEach((thumb) => {
      const mediaId = getMediaIdFromDomKey(thumb.dataset.target, data.sectionId);
      const shouldShow = !mediaId || visibleMediaIds.has(String(mediaId)) || (() => {
        const meta = mediaLookup.get(String(mediaId));
        const token = meta?.variantToken || null;
        if (!token) return true;
        return slugs.has(token) || (featuredMediaId && String(mediaId) === featuredMediaId);
      })();
      setItemVisibility(thumb, shouldShow);
    });

    ensureActiveMediaVisible(gallery, data.sectionId, featuredMediaId, visibleMediaIds);
  }

  function ensureActiveMediaVisible(gallery, sectionId, featuredMediaId, visibleMediaIds) {
    const slides = Array.from(gallery.querySelectorAll('[data-media-id]')).filter(
      (el) => !el.hasAttribute('hidden') && !el.classList.contains(HIDDEN_CLASS)
    );
    if (!slides.length) return;

    const active = gallery.querySelector('[data-media-id].is-active');
    const activeHidden =
      active && (active.hasAttribute('hidden') || active.classList.contains(HIDDEN_CLASS));

    if (active && !activeHidden) return;

    const preferredId = featuredMediaId && visibleMediaIds.has(String(featuredMediaId))
      ? `${sectionId}-${featuredMediaId}`
      : slides[0].dataset.mediaId;

    if (typeof gallery.setActiveMedia === 'function' && preferredId) {
      gallery.setActiveMedia(preferredId, true);
      return;
    }

    slides.forEach((slide) => slide.classList.remove('is-active'));
    const next = gallery.querySelector(`[data-media-id="${preferredId}"]`) || slides[0];
    next?.classList.add('is-active');
  }

  function initFromScript(script) {
    const data = parseDataScript(script);
    if (!data || !data.sectionId) return null;

    const apply = (variantId) => {
      const variant = findVariant(data, variantId);
      filterGallery(data, variant);
    };

    apply(data.selectedVariantId);

    return { data, apply };
  }

  function boot() {
    const scripts = document.querySelectorAll(`script[type="application/json"][id^="${DATA_SCRIPT_PREFIX}"]`);
    if (!scripts.length) return;

    const controllers = [];
    scripts.forEach((script) => {
      const controller = initFromScript(script);
      if (controller) controllers.push(controller);
    });

    if (!controllers.length) return;

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variantId = event?.data?.variant?.id;
        const sectionId = event?.data?.sectionId;

        controllers.forEach(({ data, apply }) => {
          if (sectionId && String(data.sectionId) !== String(sectionId)) return;
          // Run after Dawn updateMedia has finished mutating the gallery DOM.
          requestAnimationFrame(() => apply(variantId));
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
